import {
  Component,
  OnInit,
  Input,
  ViewEncapsulation,
  ViewChild,
  HostListener,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { marker, icon, latLng, Layer, tileLayer } from 'leaflet';
import { tns } from 'tiny-slider/src/tiny-slider.module';
import { Lightbox, IAlbum } from 'angular2-lightbox';
import { slugify } from '../../../utils/string';

// SERVICES
import { RequestsServices } from '../../../services/requests.service';
import { UserService } from '../../../services/user.service';
import { ToasterService } from '../../../services/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { EstablishmentsService } from '../../../services/establishments.service';
import { CompanyService } from 'src/app/services/company.service';
import { StorageService } from 'src/app/services/storage.service';
import { EstablishmentRedirectionService } from 'src/app/services/establishment-redirection.service';
import * as moment from 'moment/moment';
import { GTMService } from 'src/app/services/gtm.service';

// INTERFACES
import { ICoupon } from '../../../interfaces/coupon';

// COMPONENTS
import { Modal } from '../../../components/modal/Modal';
import { NOVE_NOVE_ABASTECE_GROUP } from '../../../components/nove-nove-checkout/nove-nove-checkout.component';

const SIX_SECONDS = 6000;

import { getEstablishmentImage, mountCdnPath } from '../../../utils/images';
import {
  getPartnershipBadge,
  PARTNERSHIP_BADGE_ICONS,
  PARTNERSHIP_BADGE_LABELS,
  PartnershipBadgeType,
} from '../../../utils/partnership-badge';

@Component({
  selector: 'app-establishment-detail',
  templateUrl: './establishment-detail.component.html',
  styleUrls: ['./establishment-detail.component.scss'],
  providers: [RequestsServices, UserService, CompanyService],
  encapsulation: ViewEncapsulation.None,
})
export class EstablishmentDetailComponent implements OnInit {
  public user: any;
  public coupon: ICoupon;
  public facebookShare: {
    title: 'Allya, ajudando a conquistar objetivos.';
    subtitle: '[estab] tem Allya! #UseAllya';
    image: 'establishment?.image?.logo ? establishment?.image?.logo : establishment?.image?.logo';
  };
  public typeBusiness;
  public establishment = null;
  public displayAllAddressesFlag = false;
  public displayMapFlag = false;
  public showShareButtons = false;
  public isFavorite = false;
  public favoriteSubmitting = false;
  public referenceAddress;
  public carouselImages: Array<IAlbum> = [];
  public isLoading = true;
  public addresses: any = [];
  public addressFilter;
  public filteredAddresses: any = [];
  public offerTitleEncoded = null;
  public campaignLink = null;

  activeAccount;

  @Input() leafletMarkers;

  @ViewChild('modalInvite')
  modal: Modal;

  @ViewChild('modalReportIrregularity')
  modalReportIrregularity: Modal;

  public markers: Layer[] = [];

  LAYER_OSM = tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 18,
    attribution: 'Open Street Map',
  });
  public options = {
    layers: [this.LAYER_OSM],
    zoom: 10,
    center: latLng(-3.7962083, -38.6261701),
  };

  backgroundImageUrl: string;
  couponModalData: object;
  imageSrcLogo: string;
  imageSrcCover: string;
  imageSrcGallery: any;
  shouldBlockOffer: undefined | boolean;
  exclusiveTooltipOpen = false;
  irregularityDescription = '';
  irregularitySubmitting = false;

  constructor(
    private translate: TranslateService,
    private toaster: ToasterService,
    private request: RequestsServices,
    private route: ActivatedRoute,
    private estabsService: EstablishmentsService,
    private userService: UserService,
    private companyService: CompanyService,
    private lightbox: Lightbox,
    private storageService: StorageService,
    private establishmentRedirectionService: EstablishmentRedirectionService,
    private gtm: GTMService,
    private router: Router
  ) {
    this.activeAccount = this.userService.getLoggedAccount();
    this.typeBusiness = this.userService.getTypeAccount();

    const languageCurrent = navigator.language;
    translate.addLangs(['en', 'pt-br']);
    translate.setDefaultLang('en');
    languageCurrent === 'pt-BR' ? translate.use('pt-br') : translate.use('en');
  }

  public ngOnInit(): void {
    this.userService.requestAccountConfig();
    window.scrollTo(0, 0);
    if (this.route.snapshot.params.establishmentSlug) {
      const establishmentSlug = this.route.snapshot.params.establishmentSlug;

      this.loadEstablishmentBySlug(establishmentSlug);
    }

    this.user = this.userService.getLoggedUser();
    this.trackingEventOnInit('page');
  }

  loadImages() {
    this.imageSrcLogo = getEstablishmentImage(this.establishment, 'logo');
    this.imageSrcCover = getEstablishmentImage(this.establishment, 'cover');

    if (this.establishment.image_key) {
      this.imageSrcGallery = this.establishment.image_key.gallery.images.map(
        (imageKey) => mountCdnPath(imageKey)
      );
    } else if (this.establishment.image && this.establishment.image.gallery) {
      this.imageSrcGallery = this.establishment.image.gallery.images;
    }

    if (this.imageSrcGallery) {
      this.carouselImages = this.imageSrcGallery.map((src) => ({ src }));
    }
  }

  getAddresses(id) {
    const currentLocation = this.storageService.get('selectedLocation');

    if (currentLocation) {
      this.estabsService
        .getEstablishmentAddresses(id, currentLocation)
        .subscribe((addresses) => {
          this.addresses = addresses;
          this.filteredAddresses = addresses;
          this.getReferenceAddress(addresses);
        });
    } else {
      this.addresses = this.establishment
        ? this.establishment.addresses || []
        : [];
    }
  }

  public getUserLevel(): string {
    return this.userService.getUserLevel();
  }

  public getPartnershipBadge(): PartnershipBadgeType {
    return getPartnershipBadge(this.establishment);
  }

  public getPartnershipBadgeIcon(): string | null {
    const badge = this.getPartnershipBadge();
    return badge ? PARTNERSHIP_BADGE_ICONS[badge] : null;
  }

  public getPartnershipBadgeLabel(): string {
    const badge = this.getPartnershipBadge();
    return badge ? PARTNERSHIP_BADGE_LABELS[badge] : '';
  }

  public getCompanyName(): string {
    const user = this.user || this.userService.getLoggedUser();
    const companies = (user && user.companies) || [];
    const companyId = user && user.companyId;
    const company = companies.find(function(item) {
      return item.id === companyId;
    }) || companies[0];

    if (company && company.name) {
      return company.name;
    }

    if (company && company.companyName) {
      return company.companyName;
    }

    return 'sua empresa';
  }

  public toggleExclusiveTooltip(event: MouseEvent): void {
    event.stopPropagation();
    this.exclusiveTooltipOpen = !this.exclusiveTooltipOpen;
  }

  @HostListener('document:click')
  public closeExclusiveTooltip(): void {
    this.exclusiveTooltipOpen = false;
  }

  /**
   * Verifica se deve bloquear as ofertas para usuários FREE em parceiros PREMIUM
   */
  public shouldBlockOffers(): boolean {
    if (this.shouldBlockOffer === undefined) {
      this.shouldBlockOffer = this.computeShouldBlockOffers();
    }

    return this.shouldBlockOffer;
  }

  private computeShouldBlockOffers(): boolean {
    if (this.establishment && !this.establishment.isPremium) {
      return false;
    }

    const user = this.userService.getLoggedUser();
    if (!user) {
      return false;
    }

    const company = user.companies.find(c => c.id === user.companyId);
    if (!company) {
      return false;
    }

    const freemiumTypes = ['b2c', 'coparticipation'];
    if (!freemiumTypes.includes(company.contractModel)) {
      return false;
    }

    return !user.freemiumSubscription || user.freemiumSubscription.isFree;
  }

  /**
   * Redireciona para a página de escolha do plano premium
   */
  public goToPremiumCheckout(): void {
    window.location.href = '/selecionar-plano';
  }

  public addMarker(lat, lon, addresObject) {
    const image =
      this.establishment &&
      this.establishment.image &&
      this.establishment.image.logo
        ? this.establishment.image.logo
        : '';
    const name =
      this.establishment && this.establishment.name
        ? this.establishment.name
        : '';
    const address = this.getAddressAsString(addresObject);
    const mapCard = `
      <div>
        <div class="card-wrapper">
          <div class="image-card" style="background-image: url('${image}')"></div>
          <div class="card-content">
            <div class="card-content-wrapper">
              <h2>${name}</h2>
              <p>${address}</p>
            </div>
          </div>
        </div>
      </div>
    `;

    const newMarker = marker([lat, lon], {
      icon: icon({
        iconSize: [50, 50],
        iconAnchor: [13, 41],
        iconUrl: 'assets/icon/marker-allya.svg',
        shadowUrl: '',
      }),
    })
      .bindPopup(mapCard)
      .openPopup();

    this.markers.push(newMarker);
  }

  public getAddressAsString(addressObject): string {
    const street = addressObject.street;
    const addressNumber = addressObject.number;
    const neighborhood = addressObject.neighborhood;
    const county = addressObject.county;
    const state = addressObject.state;
    const cep = addressObject.cep;
    return (
      street +
      ', ' +
      addressNumber +
      ', ' +
      neighborhood +
      ' - ' +
      county +
      '/' +
      state +
      ' - ' +
      cep
    );
  }

  private getReferenceAddress(addresses): any {
    if (
      this.route.snapshot.queryParams &&
      this.route.snapshot.queryParams.address
    ) {
      const addressId = this.route.snapshot.queryParams.address;
      this.referenceAddress = addresses.find(
        (address) => address.id === addressId
      );
    } else {
      this.referenceAddress = addresses[0];
    }
  }

  private loadEstablishmentBySlug(establishmentSlug, addressReference?): void {
    const filterObject: any = {
      include: {
        relation: 'addresses',
      },
      // Permite que usuários FREE acessem parceiros PREMIUM
      // O bloqueio das ofertas é feito no frontend via shouldBlockOffers()
      skipPremiumValidation: true,
    };

    // Check if was set an address as reference on URL. If so, get addresses ordered by proximity
    if (
      addressReference &&
      addressReference.location &&
      addressReference.location.lat &&
      addressReference.location.lng
    ) {
      const addressReferenceLocation = {
        lat: addressReference.location.lat,
        lng: addressReference.location.lng,
      };
      // This will bring the addresses ordered by proximity
      filterObject.include = {
        ...filterObject.include,
        scope: { where: { location: { near: addressReferenceLocation } } },
      };
    }

    const createParameters = JSON.stringify(filterObject);

    this.request
      .get({
        path: 'establishments',
        pathParam: `/getBySlug/${establishmentSlug}?filter=${createParameters}`,
      })
      .then((establishment) => {

        console.log('ESTABLISHMENT VINDO DA API:', establishment);

        this.establishment = establishment;
        this.checkFavorite(establishment.id);
        this.loadImages();
        const { offer } = this.establishment;
        this.establishment.offer.term = offer.term.replace(/&nbsp;/gi, ' ');

        if (this.establishment.offer && this.establishment.offer.title) {
          this.offerTitleEncoded = encodeURI(this.establishment.offer.title);
        }

        this.initEstablishmentDetail();

        if (establishment.group === 'standard') {
          this.getLastCoupon();
        }

        this.getAddresses(establishment.id);
      })
      .catch((response) => {
        if (response.error && response.error.name) {
          switch (response.error.name) {
            case 'EstablishmentNotFound':
              this.redirectAndShowMessage(
                '/listagem',
                'Desculpe',
                'Parceiro pode não estar ativo no momento!'
              );
              return;
            case 'EstablishmentAccessDenied':
              // Não redireciona se for apenas uma restrição de parceiro premium para usuário free
              // O bloqueio visual já é tratado pelo shouldBlockOffers()
              const user = this.userService.getLoggedUser();
              const subscription = user && user.freemiumSubscription;
              const isUserFree =
                !subscription || (subscription && subscription.isFree === true);

              // Se for usuário FREE, tenta carregar os dados do establishment do próprio erro
              // ou silenciosamente não faz nada (a página mostrará mensagem apropriada)
              if (isUserFree) {
                // Se o erro contiver dados do establishment, usa eles
                if (response.error && response.error.establishment) {
                  this.establishment = response.error.establishment;
                  this.checkFavorite(response.error.establishment.id);
                  this.loadImages();
                  this.initEstablishmentDetail();
                  if (this.establishment.id) {
                    this.getAddresses(this.establishment.id);
                  }
                } else {
                  // Se não tiver dados, mostra mensagem mas não redireciona
                  this.isLoading = false;
                  this.toaster.warn(
                    'Parceiro Premium',
                    'Este é um parceiro premium. Atualize seu plano para ter acesso completo!',
                    true,
                    SIX_SECONDS
                  );
                }
              } else {
                // Se não for caso de freemium, redireciona normalmente
                this.redirectAndShowMessage(
                  '/listagem',
                  'Ops',
                  'Parece que sua empresa não tem acesso a este parceiro!'
                );
              }
              return;
            default:
              break;
          }
        }
      });
  }

  private redirectAndShowMessage(page: string, title: string, message: string) {
    this.router.navigate([page]).then(() => {
      this.toaster.warn(title, message, true, SIX_SECONDS);
    });
  }

  private initEstablishmentDetail(): void {
    this.initCampaignLink();
    this.initMapMarkers();
    this.initCarousel();
    this.isLoading = false;
  }

  private initCampaignLink(): void {
    if (this.establishment.group !== 'online') {
      return;
    }

    let url = this.establishment.offer.sharedByValue;

    if (!url) {
      url = this.establishment.urls.url_website;
    }

    if (!url.includes('https://')) {
      if (url.includes('http://')) {
        url = url.replace('http', 'https');
      } else {
        url = `https://${url}`;
      }
    }

    if (url.includes('parceriasonline.com.br')) {
      this.campaignLink = url;
    } else {
      this.estabsService
        .completeUrlWithAnalyticsParameters(this.establishment.offer.title, url)
        .subscribe((response) => {
          this.campaignLink = response.url;
        });
    }
  }

  public initCarousel(): void {
    setTimeout(() => {
      const slider = tns({
        container: '.carousel-container',
        items: 2,
        autoplay: false,
        fixedWidth: false,
        mouseDrag: true,
        nav: false,
        controls: true,
        controlsText: ['', ''],
        loop: false,
        responsive: {
          700: {
            items: 5,
          },
        },
      });
    }, 100);
  }

  public openImage(index: number): void {
    this.lightbox.open(this.carouselImages, index);
  }

  public initMapMarkers(): void {
    if (this.establishment && this.establishment.addresses.length > 0) {
      for (const address of this.establishment.addresses) {
        if (address.location && address.location.lat && address.location.lng) {
          this.addMarker(address.location.lat, address.location.lng, address);
          this.options = {
            layers: [this.LAYER_OSM],
            zoom: 10,
            center: latLng(address.location.lat, address.location.lng),
          };
        }
      }
    }
  }

  public toggleShowShareButtons(): void {
    if (this.showShareButtons) {
      this.showShareButtons = false;
    } else {
      this.showShareButtons = true;
    }
  }

  private checkFavorite(establishmentId): void {
    this.isFavorite = false;

    console.log('CHECK FAVORITE', establishmentId);

    if (!establishmentId) {
      return;
    }

    this.estabsService
      .getFavoriteEstablishment(establishmentId)
      .then((response) => {
        this.isFavorite = !!(response && response.exists);
      })
      .catch(() => {
        this.isFavorite = false;
      });
  }

  public toggleFavorite(): void {

    console.log('TOGGLE FAVORITE',  this.establishment);

    if (!this.establishment || this.favoriteSubmitting) {
      return;
    }

    const establishmentId = this.establishment.id;
    const request = this.isFavorite
      ? this.estabsService.unfavoriteEstablishment(establishmentId)
      : this.estabsService.favoriteEstablishment(establishmentId);


      console.log('REQUEST FAVORITE', request);


    this.favoriteSubmitting = true;
    request
      .then(() => {
        this.isFavorite = !this.isFavorite;
      })
      .catch(() => {
        this.toaster.error('Erro!', 'Não foi possível atualizar o favorito. Tente novamente.', false, 4000);
      })
      .finally(() => {
        this.favoriteSubmitting = false;
      });
  }

  public displayAllAddresses(): void {
    if (this.displayAllAddressesFlag) {
      this.displayAllAddressesFlag = false;
    } else {
      this.displayAllAddressesFlag = true;
    }
  }

  public canDisplayAllAddresses(): boolean {
    return this.displayAllAddressesFlag;
  }

  public displayMap(): void {
    if (this.displayMapFlag) {
      this.displayMapFlag = false;
    } else {
      this.displayMapFlag = true;
    }
  }

  public canDisplayMap(): boolean {
    return this.displayMapFlag;
  }

  public verifyEstablishmentType(establishmentCouponType) {
    if (
      establishmentCouponType === 'ir-para-o-site-com-cupom' &&
      (this.establishment.group === 'online' ||
        this.establishment.group === 'standard') &&
      this.establishment.offer.hasCoupon === true &&
      this.establishment.offer.sharedBy === 'partner_site'
    ) {
      return true;
    }
    if (
      establishmentCouponType === 'ir-para-o-site' &&
      this.establishment.group === 'online' &&
      this.establishment.offer.hasCoupon === false
    ) {
      return true;
    }
    if (
      establishmentCouponType === 'gerar-cupom' &&
      this.establishment.group === 'standard' &&
      (this.establishment.offer.sharedBy !== 'partner_site' ||
        (this.establishment.offer.sharedBy === 'partner_site' &&
          !this.establishment.offer.sharedByValue))
    ) {
      return true;
    }
    if (
      establishmentCouponType === 'gerar-cupom' &&
      this.establishment.group === 'online' &&
      this.establishment.offer.hasCoupon === true &&
      this.establishment.offer.sharedBy !== 'partner_site'
    ) {
      return true;
    }

    if (
      establishmentCouponType === 'cinemark-primepass' &&
      (this.establishment.group === 'cinemark' ||
        this.establishment.group === 'primepass' ||
        this.establishment.group === '99' ||
        this.establishment.group === 'checkout')
    ) {
      return true;
    }
    return false;
  }

  /**
   * Verifica se o estabelecimento pertence ao agrupamento nove_nove_abastece.
   */
  public is99AbastecesGroup(): boolean {
    return Boolean(this.establishment) && this.establishment.group === NOVE_NOVE_ABASTECE_GROUP;
  }

  public openReportIrregularityModal(): void {
    this.irregularityDescription = '';
    this.modalReportIrregularity.open();
  }

  public submitReportIrregularity(): void {
    const description = this.irregularityDescription.trim();

    if (!description || !this.establishment || this.irregularitySubmitting) {
      return;
    }

    const user = this.user || this.userService.getLoggedUser();
    const account = this.activeAccount || this.userService.getLoggedAccount();
    const data = {
      employee: {
        name: user && (user.first_name || user.name),
        email: user && user.email,
        phone: user && user.phone,
        cpf: user && user.cpf,
        company_name: account && account.name,
        company_contract: account && account.customer_profile,
      },
      establishment: this.establishment,
      occurrence: {
        message: description,
      },
    };

    this.irregularitySubmitting = true;
    this.request.post('report_irregularities', data).then(
      () => {
        this.toaster.success('Sucesso!', 'Irregularidade enviada com sucesso.', false, 4000);
        this.irregularityDescription = '';
        this.modalReportIrregularity.close();
      },
      () => {
        this.toaster.error('Erro!', 'Não foi possível enviar a irregularidade. Tente novamente.', false, 4000);
      }
    ).then(() => {
      this.irregularitySubmitting = false;
    });
  }

  public generateCoupon(type): void {
    this.isLoading = true;
    const user = this.userService.getLoggedUser();
    const obj: ICoupon = {
      establishmentId: this.establishment.id,
      customerId: user.customerId,
      companyId: this.activeAccount.id,
    };
    this.sendCoupon(obj).then((result) => {
      if (type === 'gerar-cupom') {
        this.coupon = result;
        if (
          this.establishment.offer.sharedBy !== 'external_app' &&
          this.establishment.offer.sharedBy !== 'whatsapp'
        ) {
          this.dataToModal('modalCoupon');
        } else if (this.establishment.offer.sharedBy === 'external_app') {
          this.dataToModal('modalCouponApp');
        } else if (this.establishment.offer.sharedBy === 'whatsapp') {
          this.dataToModal('modalCouponWhatsapp');
        }
      } else {
        this.validateCoupon(result);
      }

      if (type === 'ir-para-o-site') {
        this.redirectPartnerSite('goToSite');
      }

      if (type === 'ir-para-o-site-com-cupom') {
        this.dataToModal('modalCouponRedirect');
      }
    });

    this.trackingEventButton(type);
    this.isLoading = false;
  }

  public redirectPartnerSite(type: string) {
    const timeOutRedirect = 4000;
    window.scrollTo(0, 0);
    this.establishmentRedirectionService.startLoading(this.campaignLink, {
      modalType: type,
    });
    setTimeout(() => {
      this.establishmentRedirectionService.stopLoading();
    }, timeOutRedirect);
  }

  public dataToModal(modalType: string) {
    this.backgroundImageUrl =
      'assets/shared/img/physical-coupon/ellipse-top.png';
    if (this.establishment && this.coupon && this.user) {
      let couponCode;
      let campaignLink;
      if (modalType === 'modalCoupon') {
        couponCode = this.coupon.code;
      } else if (modalType === 'modalCouponApp') {
        couponCode = this.coupon.code;
        campaignLink = {
          appStore: this.establishment.offer.external_app_ios,
          playStore: this.establishment.offer.external_app_android,
        };
      } else if (modalType === 'modalCouponWhatsapp') {
        couponCode = this.coupon.code;
        campaignLink = this.establishment.offer.sharedByValue;
      } else if (modalType === 'modalCouponRedirect') {
        couponCode = this.establishment.offer.defaultCoupon;
        campaignLink = this.campaignLink;
      }

      this.couponModalData = {
        partner: {
          name: this.establishment.name,
          slug: this.establishment.slug,
          logo:
            this.establishment.image && this.establishment.image.logo
              ? this.establishment.image.logo
              : undefined,
          offerTitle: this.establishment.offer.title,
          offerTerm: this.establishment.offer.term,
          guideOfUse: this.establishment.offer.guideOfUse,
          couponCode,
          campaignLink,
        },
        user: {
          name: this.user.name,
          firstName: this.user.firstName,
          cpf: this.user.cpf,
          phone: this.user.phone,
        },
        modalType,
      };
    }
    this.modal.open();
  }

  public validateCoupon(couponObj: ICoupon) {
    const params = `establishmentId=${
      couponObj.establishmentId
    }&cpf=${null}&code=${couponObj.code}`;
    this.request
      .get({ path: 'coupons', pathParam: `/validate?${params}` })
      .then((coupon) => {
        this.coupon = coupon;
      })
      .catch((err) => {
        if (err.status === 404) {
          this.toaster.error(
            'Erro',
            'Cupom inválido! Entre em contato com nossa equipe.',
            true,
            4000
          );
        }
        if (err.status === 422) {
          this.toaster.error(
            'Erro',
            `Cupom já está validado! Gerar outro cupom em "Visitar Site".`,
            true,
            4000
          );
        }
      });
  }

  public sendCoupon(couponObj: ICoupon): Promise<ICoupon> {
    return this.request
      .post('coupons', couponObj, '', '/generate')
      .then((coupon) => {
        this.coupon = coupon;
        return coupon;
      })
      .catch((err) => {
        if (err.status === 422) {
          this.toaster.error(
            'Erro',
            'Dados inválidos! Entre em contato com a nossa equipe.',
            true,
            4000
          );
        }
      });
  }

  public getLastCoupon(): any {
    const user = this.userService.getLoggedUser();
    const filter: any = {
      establishmentId: this.establishment.id,
      customerId: user.customerId,
    };

    this.request
      .get({
        path: 'coupons',
        pathParam: `/active?obj=${JSON.stringify(filter)}`,
      })
      .then((coupon) => {
        this.coupon = coupon;
      })
      .catch(() => (this.coupon = undefined));
  }

  stringNormalize(stringValue) {
    if (!stringValue) {
      return '';
    }
    return stringValue
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  filterAddress(value) {
    if (!value) {
      this.filteredAddresses = [...this.addresses];
    } else {
      const filterValue = this.stringNormalize(value).toLowerCase();

      this.filteredAddresses = [...this.addresses].filter(
        (item) =>
          this.stringNormalize(item.neiborhood).indexOf(filterValue) > -1 ||
          this.stringNormalize(item.nameUnit).indexOf(filterValue) > -1 ||
          this.stringNormalize(item.street).indexOf(filterValue) > -1 ||
          this.stringNormalize(item.number).indexOf(filterValue) > -1
      );
    }
  }

  oldTrackingEvent(name, type) {
    const category = 'admin:oferta';
    const action = `partner:${slugify(name)}`;
    const label = `btn:${type}`;

    (window as any).sendEvent(category, action, label);
  }

  public showCounter(date) {
    const daysToLastDayOfOffer = moment(date).diff(moment(), 'days');

    if (daysToLastDayOfOffer > 0 && daysToLastDayOfOffer < 30) {
      return true;
    }
    return false;
  }

  public resolveDateToTime(timeData) {
    const today = moment();
    const greatestNumberOfOneDigit = 9;
    const secondsInADay = 86400;

    const expireDate = moment(timeData).add(secondsInADay - 1, 'seconds');
    const duration = moment.duration(expireDate.diff(today));
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    return {
      lastDayOffer: !days,
      days: days < greatestNumberOfOneDigit ? `0${days}` : days,
      hours: hours < greatestNumberOfOneDigit ? `0${hours}` : hours,
      minutes: minutes < greatestNumberOfOneDigit ? `0${minutes}` : minutes,
    };
  }

  public verifyDate(showHours) {
    if (this.resolveDateToTime(showHours).lastDayOffer === true) {
      return true;
    }
    return false;
  }

  trackingEventOnInit(type: 'page', error = '') {
    const name = this.establishment ? this.establishment.name : '';
    const eventContent: any = {
      [type]: {
        environment: 'userportal',
        'error-type': `${slugify(error)}`,
        platform: 'web-resposivo',
        version: '1',
        'tipo-do-parceiro': `${slugify(name)}`,
      },
    };
    this.gtm.sendEvent(eventContent);
  }

  trackingEventButton(buttonName: string) {
    const eventContent: any = {
      event: 'interaction',
      'event-category': 'admin',
      'event-action': `${slugify(this.establishment.slug)}:click`,
      'event-label': `botao:${slugify(buttonName)}`,
    };
    this.gtm.sendEvent(eventContent);
  }
}
